import { Position } from 'context/GeoLocationContext';
import { CACHE_CONFIG, GEO_FEATURE_FLAGS } from './config';

interface StoredLocation {
  position: Position;
  addressString: string;
  timestamp: number;
  source:
    | 'user_profile'
    | 'google_estimation'
    | 'browser_geolocation'
    | 'user_search'
    | 'ip_geolocation'
    | 'url_coordinates';
}

class LocationStorage {
  private STORAGE_KEY = 'bounce_location_data';

  getStoredLocation(): StoredLocation | null {
    if (!GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as StoredLocation;

        // Check if location is still fresh
        if (Date.now() - parsed.timestamp < CACHE_CONFIG.LOCATION_TTL) {
          if (GEO_FEATURE_FLAGS.DEBUG_MODE) {
            console.log('[LocationStorage] Using stored location:', parsed.source);
          }
          return parsed;
        }

        // Remove expired location
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (e) {
      console.error('Failed to load stored location:', e);
    }

    return null;
  }

  setStoredLocation(
    position: Position,
    addressString: string,
    source: StoredLocation['source'],
  ): void {
    if (!GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
      return;
    }

    try {
      const data: StoredLocation = {
        position,
        addressString,
        timestamp: Date.now(),
        source,
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));

      if (GEO_FEATURE_FLAGS.DEBUG_MODE) {
        console.log('[LocationStorage] Stored location:', source);
      }
    } catch (e) {
      console.error('Failed to store location:', e);
    }
  }

  clearStoredLocation(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Session storage for temporary location data
  getSessionLocation(): Position | null {
    if (!GEO_FEATURE_FLAGS.USE_LOCATION_CONTEXT) {
      return null;
    }

    try {
      const stored = sessionStorage.getItem('bounce_session_location');
      if (stored) {
        return JSON.parse(stored) as Position;
      }
    } catch (e) {
      console.error('Failed to load session location:', e);
    }

    return null;
  }

  setSessionLocation(position: Position): void {
    if (!GEO_FEATURE_FLAGS.USE_LOCATION_CONTEXT) {
      return;
    }

    try {
      sessionStorage.setItem('bounce_session_location', JSON.stringify(position));
    } catch (e) {
      console.error('Failed to store session location:', e);
    }
  }
}

export const locationStorage = new LocationStorage();
